/**
 * useRegistryCache — YAML-driven cache hook.
 *
 * Reads cache strategies from domain registry YAML, auto-configures the
 * CacheManager, and provides typed cache operations scoped to a domain.
 *
 * Usage:
 *   const { get, invalidate, invalidateByEvent } = useRegistryCache("auth");
 *   const user = await get("currentUser", () => fetchUser());
 */

import { useEffect, useCallback, useRef, useMemo } from "react";

import { cacheManager, CacheConfig } from "../cache";
import { RegistryCacheStrategy } from "../config/domainRegistry";
import {
  useDomainRegistry,
  DomainName,
} from "../config/DomainRegistryProvider";
import { eventBus } from "../events/EventBus";

interface UseRegistryCacheReturn {
  /** Get data from cache or fetch via loader. Uses the YAML-configured strategy. */
  get: <T>(key: string, loader: () => Promise<T>) => Promise<T>;
  /** Manually set a value in the cache. */
  set: <T>(key: string, data: T) => void;
  /** Check if a cache key has fresh data. */
  has: (key: string) => boolean;
  /** Invalidate a specific cache key. */
  invalidate: (key: string) => void;
  /** Invalidate all cache keys listening to an event. */
  invalidateByEvent: (eventName: string) => void;
  /** Clear all cache entries for this domain. */
  clear: () => void;
  /** The raw cache strategies from YAML. */
  strategies: Record<string, RegistryCacheStrategy>;
}

export function useRegistryCache(domain: DomainName): UseRegistryCacheReturn {
  const { cacheStrategies } = useDomainRegistry(domain);
  const configuredRef = useRef(false);

  // Configure CacheManager from YAML strategies on first render
  useEffect(() => {
    if (configuredRef.current) return;
    if (!cacheStrategies || Object.keys(cacheStrategies).length === 0) return;

    // Convert YAML strategies to CacheConfig format
    const configs: Record<string, CacheConfig> = {};
    for (const [key, strategy] of Object.entries(cacheStrategies)) {
      // Prefix keys with domain to avoid collisions across domains
      const cacheKey = `${domain}:${key}`;
      configs[cacheKey] = {
        ttl: strategy.ttl,
        strategy: strategy.strategy as CacheConfig["strategy"],
        invalidateOn: strategy.invalidateOn,
        storage: strategy.storage as CacheConfig["storage"],
      };
    }

    cacheManager.configureFromStrategies(configs);

    // Subscribe to EventBus for automatic invalidation
    const allEvents = new Set<string>();
    for (const strategy of Object.values(cacheStrategies)) {
      for (const event of strategy.invalidateOn) {
        allEvents.add(event);
      }
    }

    for (const event of allEvents) {
      eventBus.subscribeGlobal(event, () => {
        cacheManager.invalidateByEvent(event);
      });
      // Track for potential cleanup
    }

    configuredRef.current = true;
  }, [cacheStrategies, domain]);

  const get = useCallback(
    <T>(key: string, loader: () => Promise<T>): Promise<T> => {
      return cacheManager.get<T>(`${domain}:${key}`, loader);
    },
    [domain],
  );

  const set = useCallback(
    <T>(key: string, data: T): void => {
      cacheManager.set(`${domain}:${key}`, data);
    },
    [domain],
  );

  const has = useCallback(
    (key: string): boolean => {
      return cacheManager.has(`${domain}:${key}`);
    },
    [domain],
  );

  const invalidate = useCallback(
    (key: string): void => {
      cacheManager.invalidate(`${domain}:${key}`);
    },
    [domain],
  );

  const invalidateByEvent = useCallback((eventName: string): void => {
    cacheManager.invalidateByEvent(eventName);
  }, []);

  const clear = useCallback((): void => {
    // Clear all keys prefixed with this domain
    // CacheManager doesn't have domain-scoped clear, so we invalidate known keys
    for (const key of Object.keys(cacheStrategies)) {
      cacheManager.invalidate(`${domain}:${key}`);
    }
  }, [domain, cacheStrategies]);

  return useMemo(
    () => ({
      get,
      set,
      has,
      invalidate,
      invalidateByEvent,
      clear,
      strategies: cacheStrategies,
    }),
    [get, set, has, invalidate, invalidateByEvent, clear, cacheStrategies],
  );
}
